import { useMemo } from "react";
import useTimePeriode from "@/hooks/useTimePeriode";
import { formatDateIndonesia } from "@/lib/utils";
import Card from "../Card";
import { Timeline, TimelineItem } from "../Timeline";
import { CalendarDays } from "lucide-react";

export default function TimelinePenerimaan() {
  const { dataPeriode, isLoadingPeriode } = useTimePeriode();

  const slugJalur = dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug;
  const isCBT = slugJalur === "cbt";
  const isTalentScouting = slugJalur === "talentscouting";

  // Helper untuk format array tanggal_cbt
  // Jika 1 tanggal -> format biasa. Jika lebih dari 1 -> gabungkan jadi rentang "Tanggal A - Tanggal B"
  const formattedJadwalTes = useMemo(() => {
    if (!dataPeriode?.tanggal_cbt || dataPeriode.tanggal_cbt.length === 0)
      return "-";

    const dates = dataPeriode.tanggal_cbt;
    if (dates.length === 1) return formatDateIndonesia(dates[0], "medium");

    const sortedDates = [...dates].sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    );
    const start = formatDateIndonesia(sortedDates[0], "medium");
    const end = formatDateIndonesia(
      sortedDates[sortedDates.length - 1],
      "medium",
    );
    return `${start} - ${end}`;
  }, [dataPeriode?.tanggal_cbt]);

  // Logika untuk menentukan fase mana yang sedang aktif saat ini
  const activePhase = useMemo(() => {
    const now = new Date().getTime();

    const getTime = (dateString?: string | null): number | null =>
      dateString ? new Date(dateString).getTime() : null;

    const tMulaiDaftar = getTime(dataPeriode?.waktu_mulai_daftar);
    const tSelesaiDaftar = getTime(dataPeriode?.waktu_selesai_daftar);
    const tPengumuman = getTime(dataPeriode?.waktu_pengumuman_kelulusan);
    const tMulaiDaftarKembali = getTime(dataPeriode?.waktu_mulai_daftarkembali);
    const tSelesaiDaftarKembali = getTime(
      dataPeriode?.waktu_selesai_daftarkembali,
    );

    // Ambil waktu tes dari array pertama jika ada
    const tMulaiTes =
      dataPeriode?.tanggal_cbt && dataPeriode.tanggal_cbt.length > 0
        ? getTime(dataPeriode.tanggal_cbt[0])
        : null;

    return {
      isMulaiDaftar: !!(
        tMulaiDaftar &&
        now >= tMulaiDaftar &&
        (!tSelesaiDaftar || now < tSelesaiDaftar)
      ),
      isSelesaiDaftar: !!(
        tSelesaiDaftar &&
        now >= tSelesaiDaftar &&
        // Jika ada jadwal tes, aktif sampai tes dimulai. Jika tidak ada, aktif sampai pengumuman.
        (tMulaiTes ? now < tMulaiTes : !tPengumuman || now < tPengumuman)
      ),
      isTes: !!(
        tMulaiTes &&
        now >= tMulaiTes &&
        (!tPengumuman || now < tPengumuman)
      ),
      isPengumuman: !!(
        tPengumuman &&
        now >= tPengumuman &&
        (!tMulaiDaftarKembali || now < tMulaiDaftarKembali)
      ),
      isMulaiDaftarKembali: !!(
        tMulaiDaftarKembali &&
        now >= tMulaiDaftarKembali &&
        (!tSelesaiDaftarKembali || now < tSelesaiDaftarKembali)
      ),
      isSelesaiDaftarKembali: !!(
        tSelesaiDaftarKembali && now >= tSelesaiDaftarKembali
      ),
    };
  }, [dataPeriode]);

  // --- SKELETON LOADING STATE (Opsional jika Anda memakainya) ---
  if (isLoadingPeriode) {
    return (
      <div className="mb-3 lg:w-[20rem] lg:sticky lg:top-28">
        <Card>
          <div className="animate-pulse flex flex-col gap-6 p-2">
            {[1, 2, 3, 4].map((item, index) => (
              <div key={item} className="flex gap-4 relative">
                {index !== 3 && (
                  <div className="absolute left-[7px] top-5 bottom-[-24px] w-0.5 bg-slate-200 dark:bg-slate-700" />
                )}
                <div className="w-4 h-4 rounded-full bg-slate-200 dark:bg-slate-700 shrink-0 mt-1" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4" />
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mb-3 lg:w-[20rem] lg:sticky lg:top-28">
      <Card className="overflow-hidden p-0">
        {/* --- HEADER UI/UX --- */}
        <div className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800 p-4 flex items-center gap-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg shrink-0">
            <CalendarDays className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-sm text-slate-900 dark:text-slate-100 leading-tight">
              Timeline Pendaftaran
            </h3>
            <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mt-0.5">
              {dataPeriode?.nama_jalur_jenis || "Memuat jalur..."}
            </p>
          </div>
        </div>

        {/* --- TIMELINE CONTENT --- */}
        <div className="p-5">
          <Timeline containerClass={"border-primary"}>
            <TimelineItem
              title="Mulai Pendaftaran"
              value={`${formatDateIndonesia(dataPeriode?.waktu_mulai_daftar || "", "medium")}`}
              isActive={activePhase.isMulaiDaftar}
              activeColorClass={"bg-primary"}
              badgeText="Wajib Tepat Waktu"
            />

            <TimelineItem
              title="Selesai Pendaftaran"
              value={`${formatDateIndonesia(dataPeriode?.waktu_selesai_daftar || "", "medium")}`}
              isActive={activePhase.isSelesaiDaftar}
              activeColorClass={"bg-primary"}
            />

            {/* Render Kondisional untuk Jadwal Tes / Wawancara */}
            {(isCBT || isTalentScouting) && (
              <TimelineItem
                title={isCBT ? "Jadwal CBT" : "Jadwal Wawancara"}
                value={formattedJadwalTes}
                isActive={activePhase.isTes}
                activeColorClass={"bg-primary"}
              />
            )}

            <TimelineItem
              title="Pengumuman Kelulusan"
              value={`${formatDateIndonesia(dataPeriode?.waktu_pengumuman_kelulusan || "", "medium")}`}
              isActive={activePhase.isPengumuman}
              activeColorClass={"bg-primary"}
            />

            {/* <TimelineItem
              title="Mulai Daftar Kembali"
              value={`${formatDateIndonesia(dataPeriode?.waktu_mulai_daftarkembali || "", "medium")}`}
              isActive={activePhase.isMulaiDaftarKembali}
              activeColorClass={"bg-primary"}
            />

            <TimelineItem
              title="Selesai Daftar Kembali"
              value={`${formatDateIndonesia(dataPeriode?.waktu_selesai_daftarkembali || "", "medium")}`}
              isActive={activePhase.isSelesaiDaftarKembali}
              activeColorClass={"bg-primary"}
            /> */}
          </Timeline>
        </div>
      </Card>
    </div>
  );
}
